import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, HistogramSeries } from 'lightweight-charts';
import { useI18n } from '../i18n';

// lightweight-charts 按 UTC 展示时间轴，把时间戳平移到本地时区
const TZ_OFFSET_SECONDS = -new Date().getTimezoneOffset() * 60;

// K 线颜色：绿涨红跌（与页面涨跌幅文案颜色一致）
const UP_COLOR = '#52c41a';
const DOWN_COLOR = '#f5222d';
// 成交量柱颜色：同色系半透明，不抢 K 线视觉
const UP_VOL_COLOR = 'rgba(82, 196, 26, 0.4)';
const DOWN_VOL_COLOR = 'rgba(245, 34, 45, 0.4)';

// 成交量简写（1234 → 1.2K）
const fmtVolume = (v) =>
  v >= 1e6 ? (v / 1e6).toFixed(2) + 'M' : v >= 1e3 ? (v / 1e3).toFixed(2) + 'K' : v.toFixed(2);

// USGNAA/USDT K 线图 + 底部成交量柱；points: [{timestamp, open, high, low, close, volume?}]（时间升序）
const KlineChart = ({ points, height = 300 }) => {
  const { t } = useI18n();
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const volSeriesRef = useRef(null);
  // 十字线悬浮时的 OHLC + 成交量读数
  const [ohlc, setOhlc] = useState(null);

  useEffect(() => {
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { textColor: '#8c8c8c', fontSize: 12 },
      grid: { vertLines: { visible: false }, horzLines: { color: '#f0f0f0' } },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.05, bottom: 0.25 } },
      timeScale: { borderColor: '#e8e8e8', timeVisible: true, secondsVisible: false }
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: UP_COLOR,
      downColor: DOWN_COLOR,
      wickUpColor: UP_COLOR,
      wickDownColor: DOWN_COLOR,
      borderVisible: false,
      priceFormat: { type: 'price', precision: 6, minMove: 0.000001 }
    });
    // 成交量：独立价格轴叠加在底部 20% 区域
    const volSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'vol',
      priceLineVisible: false,
      lastValueVisible: false
    });
    chart.priceScale('vol').applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
      visible: false
    });
    chart.subscribeCrosshairMove((param) => {
      const d = param.seriesData?.get(series);
      const v = param.seriesData?.get(volSeries);
      setOhlc(d && d.open !== undefined ? { ...d, volume: v?.value } : null);
    });
    chartRef.current = chart;
    seriesRef.current = series;
    volSeriesRef.current = volSeries;
    return () => chart.remove();
  }, []);

  useEffect(() => {
    if (!seriesRef.current) return;
    // 双重防御：过滤字段不全的点、去除重复时间戳（库要求时间严格递增，否则断言崩溃）
    const seenTimes = new Set();
    const valid = (points || [])
      .filter((p) => [p.open, p.high, p.low, p.close].every((v) => typeof v === 'number'))
      .filter((p) => {
        if (seenTimes.has(p.timestamp)) return false;
        seenTimes.add(p.timestamp);
        return true;
      });
    seriesRef.current.setData(
      valid.map((p) => ({
        time: p.timestamp / 1000 + TZ_OFFSET_SECONDS,
        open: p.open,
        high: p.high,
        low: p.low,
        close: p.close
      }))
    );
    // 成交量柱：无 volume 的点不画；颜色跟随该根 K 线涨跌
    volSeriesRef.current.setData(
      valid
        .filter((p) => typeof p.volume === 'number')
        .map((p) => ({
          time: p.timestamp / 1000 + TZ_OFFSET_SECONDS,
          value: p.volume,
          color: p.close >= p.open ? UP_VOL_COLOR : DOWN_VOL_COLOR
        }))
    );
    chartRef.current?.timeScale().fitContent();
  }, [points]);

  return (
    <div style={{ position: 'relative' }}>
      {ohlc && (
        <div
          style={{
            position: 'absolute',
            top: 4,
            left: 8,
            zIndex: 2,
            fontSize: 12,
            color: '#595959',
            background: 'rgba(255,255,255,0.85)',
            padding: '2px 8px',
            borderRadius: 4,
            pointerEvents: 'none'
          }}
        >
          {t('开_k')} {ohlc.open.toFixed(6)}　{t('高_k')} {ohlc.high.toFixed(6)}　{t('低_k')} {ohlc.low.toFixed(6)}　{t('收_k')} {ohlc.close.toFixed(6)}
          {typeof ohlc.volume === 'number' && <>　{t('量_k')} {fmtVolume(ohlc.volume)}</>}
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height }} />
    </div>
  );
};

export default KlineChart;
